import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Banknote, FileText, Landmark, Receipt, Undo2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import { getCountries } from "../../../api/General/Country";

import {
  correctPlatformInvoice,
  formatMoney,
  issuePlatformDraftInvoice,
  raisePlatformManualInvoice,
  recordPlatformInvoicePayment,
  setPlatformTaxCountry,
  type PlatformBillingInvoiceRow,
  type PlatformTaxProfileView,
} from "../../../api/General/PlatformBilling";
import {
  DialogIcon,
  ErrorNote,
  ReasonField,
  fieldClass,
  firstError,
} from "./PlatformBillingDialogs";
import { invoiceLabel, taxTreatmentLabel } from "./billingLabels";

/*
 * CO-122 / CO-125 — THE HAND-COLLECTED INVOICE REGISTER AND THE TAX-COUNTRY CONTROL.
 *
 * These four dialogs share the vocabulary the ten existing operator dialogs already established: a
 * mandatory reason validated on blur, an outcome-named submit, and no field for any number the server
 * computes. Three rules are specific to this file and are load-bearing:
 *
 *   1. NO VAT FIELD, ANYWHERE. Amounts on a manual invoice are genuinely operator-supplied (that is
 *      what "negotiated, manually invoiced" means) but tax never is — the invoice service derives it
 *      from the tenant's treatment. A tax input here would let an operator print a wrong figure on a
 *      legal document.
 *   2. RECORDING A PAYMENT IS NOT "MARK AS PAID". It settles a hand-raised invoice IN FULL against
 *      money that has already arrived. The caller decides whether to offer it (`canRecordPayment`);
 *      this dialog states the consequence plainly rather than relying on that gate alone.
 *   3. A CORRECTION IS A CREDIT NOTE. The issued document is never edited — it has already consumed a
 *      number from the legal sequence — so the amounts collected here are what is credited BACK.
 */

interface InvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number | null;
  companyName: string;
  invoice: PlatformBillingInvoiceRow | null;
  onSuccess: () => void;
}

/** A quiet definition row. Used instead of a nested card, which the design system bans. */
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground tabular-nums text-end">{value}</dd>
    </div>
  );
}

/* ═══════════════ 1. Record a hand-collected payment (the CO-94 lever) ═══════════════ */

export function RecordPaymentDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  invoice,
  onSuccess,
}: InvoiceDialogProps) {
  const { t } = useTranslation();
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReference("");
      setPaidAt("");
      setReason("");
      setTouched(false);
      setError(null);
    }
  }, [open]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const reasonInvalid = touched && reason.trim().length === 0;

  const submit = async () => {
    if (!companyId || !invoice) return;
    if (reason.trim().length === 0) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await recordPlatformInvoicePayment(companyId, invoice.id, {
        invoiceId: invoice.id,
        // A date-only input means midnight local. Sending the bare date keeps the VALUE date the
        // operator typed, rather than shifting it a day either way through the browser's offset.
        paidAtUtc: paidAt ? `${paidAt}T00:00:00Z` : null,
        reference: reference.trim() || null,
        reason: reason.trim(),
      });
      const msg = firstError(result.errors);
      if (msg) {
        setError(msg);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setError(t("Could not record the payment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Banknote size={18} aria-hidden="true" />
            </DialogIcon>
            <div>
              <DialogTitle>{t("Record a payment already received")}</DialogTitle>
              <DialogDescription>
                {t(
                  "Money collected outside the billing engine, usually by bank transfer. Nothing is charged and no card is touched.",
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <dl className="rounded-md border border-border px-3 py-1 divide-y divide-border">
            <Fact label={t("Company")} value={companyName} />
            <Fact label={t("Invoice")} value={invoiceLabel(invoice, t)} />
            <Fact
              label={t("Still owing")}
              value={formatMoney(invoice?.amountDue, invoice?.currency)}
            />
          </dl>

          <p className="text-sm text-muted-foreground">
            {t(
              "This settles the invoice IN FULL. A part-payment has nothing to attribute it to, and recording one as full would silence dunning for money that has not arrived.",
            )}
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="record-paid-at">
                {t("Value date")}
              </label>
              <input
                id="record-paid-at"
                type="date"
                max={today}
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
                className={fieldClass(false)}
              />
              <p className="text-xs text-muted-foreground">
                {t("The day the transfer landed. Leave empty for today.")}
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="record-reference">
                {t("Reference")}
              </label>
              <input
                id="record-reference"
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t("Transfer or cheque number")}
                className={fieldClass(false)}
              />
              <p className="text-xs text-muted-foreground">
                {t("Billing detail only. Never anything personal.")}
              </p>
            </div>
          </div>

          <ReasonField
            id="record-payment-reason"
            value={reason}
            onChange={setReason}
            invalid={reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. bank transfer confirmed by finance on 3 August")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={submitting}>
            {t("Record payment in full")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ 2. Raise a manual invoice ═══════════════ */

interface LineDraft {
  descriptionKey: string;
  quantity: string;
  unitPriceNet: string;
}

const DESCRIPTION_KEY_OPTIONS = [
  { value: "billing.invoice.line.subscriptionTerm", label: "Subscription term" },
  { value: "billing.invoice.line.seatAddition", label: "Additional seats" },
  { value: "billing.invoice.line.negotiatedFee", label: "Negotiated fee" },
  { value: "billing.invoice.line.setupFee", label: "Setup fee" },
];

export function RaiseInvoiceDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  currency,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number | null;
  companyName: string;
  currency: string;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<LineDraft[]>([
    { descriptionKey: DESCRIPTION_KEY_OPTIONS[0].value, quantity: "1", unitPriceNet: "" },
  ]);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [notes, setNotes] = useState("");
  const [issueNow, setIssueNow] = useState(true);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLines([{ descriptionKey: DESCRIPTION_KEY_OPTIONS[0].value, quantity: "1", unitPriceNet: "" }]);
      setPeriodStart("");
      setPeriodEnd("");
      setDueAt("");
      setNotes("");
      setIssueNow(true);
      setReason("");
      setTouched(false);
      setError(null);
    }
  }, [open]);

  const netTotal = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const q = Number(l.quantity);
        const p = Number(l.unitPriceNet);
        return sum + (Number.isFinite(q) && Number.isFinite(p) ? q * p : 0);
      }, 0),
    [lines],
  );

  const linesValid = lines.every(
    (l) => Number(l.quantity) > 0 && Number.isFinite(Number(l.unitPriceNet)) && Number(l.unitPriceNet) > 0,
  );
  const reasonInvalid = touched && reason.trim().length === 0;
  const canSubmit = linesValid && reason.trim().length > 0;

  const updateLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const submit = async () => {
    if (!companyId) return;
    if (!canSubmit) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await raisePlatformManualInvoice(companyId, {
        currency,
        lines: lines.map((l) => ({
          descriptionKey: l.descriptionKey,
          quantity: Number(l.quantity),
          unitPriceNet: Number(l.unitPriceNet),
          periodStartUtc: periodStart ? `${periodStart}T00:00:00Z` : null,
          periodEndUtc: periodEnd ? `${periodEnd}T00:00:00Z` : null,
        })),
        periodStartUtc: periodStart ? `${periodStart}T00:00:00Z` : null,
        periodEndUtc: periodEnd ? `${periodEnd}T00:00:00Z` : null,
        dueAtUtc: dueAt ? `${dueAt}T00:00:00Z` : null,
        notes: notes.trim() || null,
        issue: issueNow,
        reason: reason.trim(),
      });
      const msg = firstError(result.errors);
      if (msg) {
        setError(msg);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setError(t("Could not raise the invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Receipt size={18} aria-hidden="true" />
            </DialogIcon>
            <div>
              <DialogTitle>{t("Raise an invoice for {{company}}", { company: companyName })}</DialogTitle>
              <DialogDescription>
                {t("A manual document for a negotiated arrangement, priced by you and taxed by the system.")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-2">
            {lines.map((line, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_5rem_8rem_2rem] sm:items-end">
                <div className="space-y-1.5">
                  {i === 0 && (
                    <label className="text-sm font-medium text-foreground">{t("What for")}</label>
                  )}
                  <Select
                    options={DESCRIPTION_KEY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                    value={line.descriptionKey}
                    onChange={(v: string) => updateLine(i, { descriptionKey: v })}
                  />
                </div>
                <div className="space-y-1.5">
                  {i === 0 && (
                    <label className="text-sm font-medium text-foreground" htmlFor={`line-qty-${i}`}>
                      {t("Qty")}
                    </label>
                  )}
                  <input
                    id={`line-qty-${i}`}
                    type="number"
                    min={1}
                    value={line.quantity}
                    onChange={(e) => updateLine(i, { quantity: e.target.value })}
                    className={`${fieldClass(false)} tabular-nums`}
                  />
                </div>
                <div className="space-y-1.5">
                  {i === 0 && (
                    <label className="text-sm font-medium text-foreground" htmlFor={`line-price-${i}`}>
                      {t("Unit price ({{currency}}, ex-VAT)", { currency })}
                    </label>
                  )}
                  <input
                    id={`line-price-${i}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.unitPriceNet}
                    onChange={(e) => updateLine(i, { unitPriceNet: e.target.value })}
                    className={`${fieldClass(false)} tabular-nums`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setLines((prev) => prev.filter((_, x) => x !== i))}
                  disabled={lines.length === 1}
                  aria-label={t("Remove line {{n}}", { n: i + 1 })}
                  className="h-9 rounded-md text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:hover:text-muted-foreground"
                >
                  <Undo2 size={15} aria-hidden="true" className="mx-auto" />
                </button>
              </div>
            ))}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() =>
                  setLines((prev) => [
                    ...prev,
                    { descriptionKey: DESCRIPTION_KEY_OPTIONS[0].value, quantity: "1", unitPriceNet: "" },
                  ])
                }
                className="text-sm font-medium text-primary hover:underline"
              >
                {t("Add a line")}
              </button>
              <p className="text-sm text-muted-foreground">
                {t("Net total")}{" "}
                <span className="font-medium text-foreground tabular-nums">
                  {formatMoney(netTotal, currency)}
                </span>
              </p>
            </div>
          </div>

          {/*
            The one thing an operator will look for and must not find. Saying why is cheaper than
            fielding the question, and it forecloses a future "just add a VAT box" change request.
          */}
          <p className="text-sm text-muted-foreground">
            {t(
              "VAT is not entered here. The system computes it from this tenant's tax treatment when the invoice is issued, so the figure on the document is always the one the tax rules produce.",
            )}
          </p>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="inv-period-start">
                {t("Period from")}
              </label>
              <input
                id="inv-period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className={fieldClass(false)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="inv-period-end">
                {t("Period to")}
              </label>
              <input
                id="inv-period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className={fieldClass(false)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="inv-due">
                {t("Due")}
              </label>
              <input
                id="inv-due"
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className={fieldClass(false)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="inv-notes">
              {t("Note on the invoice")}
            </label>
            <input
              id="inv-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("Printed on the document. Optional.")}
              className={fieldClass(false)}
            />
          </div>

          <label className="flex items-start gap-2.5 text-sm text-foreground">
            <input
              type="checkbox"
              checked={issueNow}
              onChange={(e) => setIssueNow(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-primary/20"
            />
            <span>
              {t("Issue it now")}
              <span className="block text-xs text-muted-foreground">
                {t(
                  "Issuing burns the next number in the legal sequence and freezes the tax treatment. Leave this off to keep it as an unnumbered draft.",
                )}
              </span>
            </span>
          </label>

          <ReasonField
            id="raise-invoice-reason"
            value={reason}
            onChange={setReason}
            invalid={reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. annual term agreed by contract, collected by transfer")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
            {issueNow ? t("Raise and issue") : t("Save as draft")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ 3. Issue a draft ═══════════════ */

export function IssueDraftDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  invoice,
  onSuccess,
}: InvoiceDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (!companyId || !invoice) return;
    if (reason.trim().length === 0) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await issuePlatformDraftInvoice(companyId, invoice.id, reason.trim());
      const msg = firstError(result.errors);
      if (msg) {
        setError(msg);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setError(t("Could not issue the invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <FileText size={18} aria-hidden="true" />
            </DialogIcon>
            <div>
              <DialogTitle>{t("Issue this draft")}</DialogTitle>
              <DialogDescription>
                {t("It takes the next number in the legal sequence and becomes visible to the customer.")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <dl className="rounded-md border border-border px-3 py-1 divide-y divide-border">
            <Fact label={t("Company")} value={companyName} />
            <Fact label={t("Invoice")} value={invoiceLabel(invoice, t)} />
            <Fact label={t("Total")} value={formatMoney(invoice?.totalGross, invoice?.currency)} />
          </dl>
          <p className="text-sm text-muted-foreground">
            {t(
              // ⚠️ NO COLON. `i18n.js` sets `nsSeparator: ':'`, so a key containing a colon is
              // split into namespace + key and renders EMPTY (the CO-62 failure).
              "Safe to repeat. An invoice that already carries a number comes back unchanged rather than taking a second one.",
            )}
          </p>
          <ReasonField
            id="issue-draft-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reason.trim().length === 0}
            onBlur={() => setTouched(true)}
            hint={t("e.g. re-issuing after the first attempt failed")}
          />
          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={submitting}>
            {t("Issue and number it")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ 4. Correct an issued invoice (credit note) ═══════════════ */

export function CorrectInvoiceDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  invoice,
  onSuccess,
}: InvoiceDialogProps) {
  const { t } = useTranslation();
  const [net, setNet] = useState("");
  const [tax, setTax] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNet("");
      setTax("");
      setReason("");
      setTouched(false);
      setError(null);
    }
  }, [open]);

  const netValue = Number(net);
  const taxValue = tax === "" ? 0 : Number(tax);
  const amountsValid = Number.isFinite(netValue) && netValue > 0 && Number.isFinite(taxValue) && taxValue >= 0;
  const canSubmit = amountsValid && reason.trim().length > 0;

  const submit = async () => {
    if (!companyId || !invoice) return;
    if (!canSubmit) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await correctPlatformInvoice(companyId, {
        invoiceId: invoice.id,
        net: netValue,
        tax: taxValue,
        reason: reason.trim(),
      });
      const msg = firstError(result.errors);
      if (msg) {
        setError(msg);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setError(t("Could not correct the invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="amber">
              <Undo2 size={18} aria-hidden="true" />
            </DialogIcon>
            <div>
              <DialogTitle>{t("Credit part of this invoice back")}</DialogTitle>
              <DialogDescription>
                {t(
                  "The invoice itself is never edited. A credit note is raised against it, which is what the tax rules require.",
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <dl className="rounded-md border border-border px-3 py-1 divide-y divide-border">
            <Fact label={t("Company")} value={companyName} />
            <Fact label={t("Invoice")} value={invoiceLabel(invoice, t)} />
            <Fact label={t("Total")} value={formatMoney(invoice?.totalGross, invoice?.currency)} />
          </dl>

          {/*
            Tax IS collected here, unlike on the raise path, and the difference is not arbitrary: a
            credit note reverses a document whose tax was already computed and frozen, so the amount
            being reversed is a historical fact rather than a rate to apply. Saying so stops a later
            pass from "harmonising" the two dialogs.
          */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="correct-net">
                {t("Net to credit back")} <span className="text-destructive">*</span>
              </label>
              <input
                id="correct-net"
                type="number"
                min={0}
                step="0.01"
                value={net}
                onChange={(e) => setNet(e.target.value)}
                className={`${fieldClass(touched && !amountsValid)} tabular-nums`}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground" htmlFor="correct-tax">
                {t("Tax to credit back")}
              </label>
              <input
                id="correct-tax"
                type="number"
                min={0}
                step="0.01"
                value={tax}
                onChange={(e) => setTax(e.target.value)}
                className={`${fieldClass(false)} tabular-nums`}
              />
              <p className="text-xs text-muted-foreground">
                {t("The tax that was charged on the part being reversed. Zero if none was.")}
              </p>
            </div>
          </div>

          <ReasonField
            id="correct-invoice-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reason.trim().length === 0}
            onBlur={() => setTouched(true)}
            hint={t("e.g. billed for 12 seats, the agreement was 10")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button variant="destructive" onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
            {t("Raise the credit note")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════ 5. Tax country (CO-125) ═══════════════ */

/*
 * 🔴 THERE IS DELIBERATELY NO CLIENT-SIDE RATE PREVIEW. An earlier draft of this dialog mirrored
 * `TaxService`'s switch to show "after: KSA VAT · 15%", and that mirror was WRONG in the case that
 * mattered most.
 *
 * The server's answer for `SA` is not a function of the country at all. It depends on
 * `Billing:Ksa:VatRegistered` — a ZATCA Phase-2 gate that is deliberately OFF, and whose own comment
 * says flipping it while we emit plain PDFs is a WORSE compliance position than the zero-rated export
 * we launch with — and then on whether the customer has a TRN. So `SA` resolves to one of three
 * things: KsaStandard15 at 15%, ZeroRatedExport at 0%, or ZeroRatedExport at 0% AND self-serve
 * blocked. `AE` likewise reads the configurable `settings.UaeStandardPercent`, not a literal 5.
 *
 * A client cannot know any of that. The mirror would have promised an operator "15%", they would
 * have saved with that justification, and every future invoice would have been issued at 0% — with
 * the Tax section of this very sheet then showing 0% and silently contradicting the panel they acted
 * on. Showing the country truthfully and the rate NOT AT ALL is strictly better than showing a
 * confident wrong number. The resolved treatment is read back from the server after saving.
 */

export function TaxCountryDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  taxProfile,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: number | null;
  companyName: string;
  taxProfile: PlatformTaxProfileView | null;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget("");
    setReason("");
    setTouched(false);
    setError(null);
    void (async () => {
      try {
        const result = await getCountries();
        const rows = (result.payload ?? [])
          // 🔴 "ZZ" is the unknown-country SENTINEL, not a country. The server refuses it by name, so
          // offering it would be an option that can only ever fail.
          .filter((c) => c.iso && c.iso.toUpperCase() !== "ZZ")
          .map((c) => ({ value: c.iso.toUpperCase(), label: `${c.name} (${c.iso.toUpperCase()})` }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(rows);
      } catch {
        setError(t("Could not load the country list."));
      }
    })();
  }, [open, t]);

  const unchanged =
    !!taxProfile && !!target && target.toUpperCase() === taxProfile.taxCountry.toUpperCase();
  const canSubmit = !!target && !unchanged && reason.trim().length > 0;

  const submit = async () => {
    if (!companyId) return;
    if (!canSubmit) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await setPlatformTaxCountry(companyId, {
        taxCountry: target,
        reason: reason.trim(),
      });
      const msg = firstError(result.errors);
      if (msg) {
        setError(msg);
        return;
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      setError(t("Could not change the tax country."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="amber">
              <Landmark size={18} aria-hidden="true" />
            </DialogIcon>
            <div>
              <DialogTitle>{t("Change where {{company}} is taxed", { company: companyName })}</DialogTitle>
              <DialogDescription>
                {t(
                  "The country decides both the VAT rate and the tax statement printed on every invoice.",
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {!taxProfile ? (
            <p className="text-sm text-foreground">
              {t(
                "This company has no billing profile yet, so there is nothing to tax. The profile is created on the tenant's first checkout or when they save their billing details.",
              )}
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">{t("Tax country")}</label>
                <Select
                  options={countries}
                  value={target}
                  searchable
                  placeholder={t("Choose a country")}
                  onChange={(v: string) => setTarget(v)}
                />
              </div>

              {/*
                "Today" is SERVER-SUPPLIED and therefore true. "After" deliberately shows the country
                ONLY — see the block comment above `TaxCountryDialog` for why a client-side rate here
                was a confident lie. The consequence is still disclosed, as the sentence it actually
                is, rather than as a number we cannot compute.
              */}
              <dl className="rounded-md border border-border px-3 py-1 divide-y divide-border">
                <Fact
                  label={t("Taxed today in")}
                  value={
                    <>
                      {taxProfile.taxCountry}
                      <span className="mx-1.5 text-muted-foreground/50">·</span>
                      {t(taxTreatmentLabel(String(taxProfile.resolvedTaxTreatment)))}
                      <span className="mx-1.5 text-muted-foreground/50">·</span>
                      {taxProfile.resolvedTaxRatePercent}%
                    </>
                  }
                />
                <Fact
                  label={t("Changing to")}
                  value={
                    target ? (
                      <span className={unchanged ? "text-muted-foreground" : "text-foreground"}>
                        {target}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{t("Choose a country first")}</span>
                    )
                  }
                />
              </dl>

              {target && !unchanged && (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "The VAT rate and the wording printed on the invoice are worked out by the system from this country, its registration status and the customer's own tax number. The resolved treatment appears on this page as soon as you save.",
                  )}
                </p>
              )}

              {unchanged && (
                <p className="text-sm text-muted-foreground">
                  {t("That is already this company's tax country. Nothing would change.")}
                </p>
              )}

              {/*
                The sentence that prevents a support ticket. An operator who changes this expecting
                already-issued documents to be reprinted will wait for a correction that never comes.
              */}
              <p className="text-sm text-muted-foreground">
                {t(
                  "This affects future invoices only. Anything already issued keeps the treatment it was issued under and is never rewritten. To fix an issued document, credit it back instead.",
                )}
              </p>

              <ReasonField
                id="tax-country-reason"
                value={reason}
                onChange={setReason}
                invalid={touched && reason.trim().length === 0}
                onBlur={() => setTouched(true)}
                hint={t("e.g. entity relocated to KSA, confirmed by their finance team")}
              />
            </>
          )}

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {taxProfile ? t("Cancel") : t("Close")}
          </Button>
          {taxProfile && (
            <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
              {t("Change the tax country")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
